import { z } from 'zod';

/**
 * `PolicyProduct` — a booking product gated by the Voll `policy-api`
 * `policyGetAccessModal` query.
 *
 * SCOPE (decided 2026-06-12): ONLY the product modals the agent/card needs. The
 * full policy modal also carries `approval`/`trip`/`seat`/`qrcode` and a nested
 * `module` block (admin/limit/report/…); those are intentionally NOT mirrored
 * here — they are permission/role concerns, not booking-product access. Add them
 * later only when a consumer needs them.
 */
export const PolicyProductSchema = z.enum([
  'flight',
  'hotel',
  'rental',
  'mobility',
  'bus',
  'expense',
  'expenseKm',
  'pay',
  'wallet',
]);
export type PolicyProduct = z.infer<typeof PolicyProductSchema>;

/**
 * `PolicyAccess` — the traveler's per-product access, sourced from
 * `policyGetAccessModal` (customer data L1 enrichment), stored as the LIST of
 * products the traveler HAS access to (the modal flags that resolved truthy). A
 * product absent from the list = no access. The producer (customer-api policy
 * gateway) builds this list from the upstream booleans.
 */
export const PolicyAccessSchema = z.array(PolicyProductSchema);
export type PolicyAccess = z.infer<typeof PolicyAccessSchema>;

/**
 * `ApprovalDefaults` — the traveler's default rateio (company + cost center)
 * for booking, sourced from `business.user_costcenter` (the row with
 * `default: true`, falling back to any row). Company/cost-center names are
 * denormalized on the source document.
 *
 * `costCenter` is REQUIRED here: the business-service returns a null cost center
 * for the "all cost centers" case (`costcenter.id === 0`), but the booking flow
 * needs a concrete pair, so the producer omits `approvalDefaults` entirely in
 * that case (fail-closed) rather than emitting a half-filled object.
 */
export const ApprovalDefaultsSchema = z.object({
  company: z.object({ id: z.string().min(1), name: z.string().min(1) }),
  costCenter: z.object({ id: z.string().min(1), name: z.string().min(1) }),
});
export type ApprovalDefaults = z.infer<typeof ApprovalDefaultsSchema>;
